import React, { forwardRef } from 'react';

/**
 * Tailwind primary button — matches the design package's `.btn` spec exactly
 * (radii, padding, shadows, gradient option). Variants and sizes use a flat
 * lookup so they're easy to scan.
 *
 * Variants:
 *   primary     — solid violet, our default CTA
 *   gradient    — primary→secondary 135° gradient (premium / hero CTA)
 *   secondary   — surface color with border, for "less urgent" actions
 *   ghost       — transparent, for icon-row / inline actions
 *   destructive — solid error red
 *
 * Sizes:
 *   sm | md (default) | lg
 *
 * Pass `loading` to show a spinner and disable interaction.
 */
const VARIANTS = {
  primary:
    'bg-primary text-white shadow-primary-glow ' +
    'hover:bg-primary-hover hover:-translate-y-px hover:shadow-[0_6px_16px_rgba(139,92,246,0.40)] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
  gradient:
    'text-white shadow-[0_6px_18px_rgba(139,92,246,0.35)] ' +
    'bg-gradient-to-br from-primary to-secondary ' +
    'hover:-translate-y-px hover:shadow-[0_8px_22px_rgba(139,92,246,0.45)] ' +
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
  secondary:
    'bg-surface text-fg border border-border-default ' +
    'hover:bg-soft disabled:opacity-50',
  ghost:
    'bg-transparent text-fg-muted hover:bg-soft hover:text-fg ' +
    'disabled:opacity-50',
  destructive:
    'bg-error text-white hover:brightness-110 disabled:opacity-50',
};

const SIZES = {
  sm: 'px-3 py-1.5 text-[13px] leading-5 rounded-[8px] gap-1.5',
  md: 'px-4 py-[9px] text-sm leading-5 rounded-md gap-2',
  lg: 'px-5 py-2.5 text-base leading-6 rounded-lg gap-2',
};

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-4 h-4',
  lg: 'w-5 h-5',
};

function Spinner({ size }) {
  return (
    <svg
      className={`${ICON_SIZES[size]} animate-spin`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="60"
        strokeDashoffset="20"
      />
    </svg>
  );
}

const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    type = 'button',
    className = '',
    children,
    onClick,
    ...rest
  },
  ref
) {
  const base =
    'inline-flex items-center justify-center font-semibold select-none ' +
    'transition-all duration-150 disabled:cursor-not-allowed ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
  const classes = [
    base,
    VARIANTS[variant] ?? VARIANTS.primary,
    SIZES[size] ?? SIZES.md,
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      onClick={loading ? undefined : onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? <Spinner size={size} /> : leadingIcon}
      {children}
      {!loading && trailingIcon}
    </button>
  );
});

export default Button;
